/* Training schedule: the plan day of a date (the sessions[date] stamp first,
 * then the cycle from cycleAnchor), training dates, a session streak and a
 * weekly streak that planned rest never breaks, this week's compliance so far
 * and missed planned days. Pure functions over the state; the Python twin is
 * src/fitlogic/schedule.py and both are pinned by vectors/schedule.json.
 *
 * Dates are YYYY-MM-DD workout days (the caller applies the 04:00 rollover,
 * e.g. todayKey()). Weeks run Monday to Sunday. Nothing before the first
 * logged session counts as missed: tracking hadn't started. */

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const DEFAULT_TRAINING_DAYS = 4;

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function own(obj, key) {
  return isObj(obj) && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
}

/* Days since the epoch for a real YYYY-MM-DD key, else null (2026-02-30 too). */
function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function keyOf(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

/* Monday = 0; day 0 (1970-01-01) was a Thursday. */
function monIndex(n) {
  return (((n + 3) % 7) + 7) % 7;
}

/**
 * The Monday of a date's week.
 * @param {string} date YYYY-MM-DD
 * @returns {string|null} YYYY-MM-DD, null for an invalid date
 */
export function mondayOf(date) {
  const n = dayNumber(date);
  return n === null ? null : keyOf(n - monIndex(n));
}

/**
 * Position in the plan cycle, the same maths as the app's cycleIndexForDate
 * (whole calendar days from the anchor, wrapped into 0..planLength-1).
 * @param {string} date YYYY-MM-DD
 * @param {number} planLength plan.length; below 1 counts as 1
 * @param {string} anchor cycleAnchor YYYY-MM-DD (the date of plan[0])
 * @returns {number|null} null when date or anchor is not a valid date
 */
export function cycleIndex(date, planLength, anchor) {
  const d = dayNumber(date);
  const a = dayNumber(anchor);
  if (d === null || a === null) return null;
  const len = Number.isFinite(planLength) && planLength >= 1 ? Math.floor(planLength) : 1;
  return (((d - a) % len) + len) % len;
}

function findDay(plan, test) {
  return plan.findIndex(day => isObj(day) && test(day));
}

/**
 * The plan day of a date. sessions[date].planDayId wins when that id is still
 * in the plan, then sessions[date].planDayName (exact, then ignoring case and
 * spaces, since a plan reset makes new ids), then the cycle.
 * @param {object} state needs plan; reads sessions, cycleAnchor
 * @param {string} date YYYY-MM-DD
 * @param {string} [fallbackAnchor] used when state.cycleAnchor is missing or
 *   invalid (the app's default is the Monday of the current week)
 * @returns {{index:number, day:object, source:'stamp'|'cycle'}|null} day is
 *   the plan entry itself; null without a plan, a valid date or an anchor
 */
export function planDayFor(state, date, fallbackAnchor) {
  const plan = isObj(state) && Array.isArray(state.plan) ? state.plan : [];
  if (!plan.length || dayNumber(date) === null) return null;
  const stamp = own(state.sessions, date);
  if (isObj(stamp)) {
    let index = -1;
    if (typeof stamp.planDayId === 'string' && stamp.planDayId) index = findDay(plan, d => d.id === stamp.planDayId);
    if (index < 0 && typeof stamp.planDayName === 'string' && stamp.planDayName.trim()) {
      const want = stamp.planDayName.trim().toLowerCase();
      index = findDay(plan, d => d.name === stamp.planDayName);
      if (index < 0) index = findDay(plan, d => typeof d.name === 'string' && d.name.trim().toLowerCase() === want);
    }
    if (index >= 0) return { index, day: plan[index], source: 'stamp' };
  }
  const anchor = dayNumber(state.cycleAnchor) !== null ? state.cycleAnchor : fallbackAnchor;
  const index = cycleIndex(date, plan.length, anchor);
  if (index === null) return null;
  return { index, day: plan[index], source: 'cycle' };
}

/**
 * Whether a date has a session: at least one done set in logs[date].
 * @param {object} state reads logs
 * @param {string} date YYYY-MM-DD
 * @returns {boolean}
 */
export function isTrainingDate(state, date) {
  const day = own(isObj(state) ? state.logs : null, date);
  if (!isObj(day)) return false;
  return Object.keys(day).some(name => Array.isArray(day[name]) && day[name].some(s => isObj(s) && s.done));
}

/* 'rest' for a planned rest day, 'training' for a planned training day and
 * for a date without plan info (then every day counts, like a calendar). */
function plannedKind(state, date, fallbackAnchor) {
  const p = planDayFor(state, date, fallbackAnchor);
  return p && isObj(p.day) && p.day.isRest ? 'rest' : 'training';
}

/* Sorted training dates up to `last` (a day number; null = no limit). */
function trainingDays(state, last) {
  const logs = isObj(state) && isObj(state.logs) ? state.logs : {};
  return Object.keys(logs)
    .map(dayNumber)
    .filter(n => n !== null && (last === null || n <= last) && isTrainingDate(state, keyOf(n)))
    .sort((a, b) => a - b);
}

/**
 * Session streak: consecutive sessions walking back from today. A planned
 * rest day neither breaks nor counts (a session on it does count), a still
 * pending today doesn't break, a planned training day without a session
 * breaks.
 * @param {object} state reads logs, plan, sessions, cycleAnchor
 * @param {string} today workout day YYYY-MM-DD; the Monday of its week is the
 *   fallback cycle anchor
 * @returns {{current:number, best:number}} best is the longest run ever,
 *   current included
 */
export function sessionStreak(state, today) {
  const t = dayNumber(today);
  if (t === null) return { current: 0, best: 0 };
  const done = new Set(trainingDays(state, t));
  if (!done.size) return { current: 0, best: 0 };
  const fallback = mondayOf(today);
  let run = 0;
  let best = 0;
  for (let n = Math.min(...done); n <= t; n += 1) {
    if (done.has(n)) {
      run += 1;
      best = Math.max(best, run);
    } else if (n !== t && plannedKind(state, keyOf(n), fallback) === 'training') {
      run = 0;
    }
  }
  return { current: run, best };
}

function trainingTarget(state) {
  const v = isObj(state) && isObj(state.profile) ? state.profile.trainingDays : null;
  if (!Number.isFinite(v) || v < 1) return DEFAULT_TRAINING_DAYS;
  return Math.min(Math.floor(v), 7);
}

/**
 * Weekly streak: consecutive Mon-Sun weeks with at least
 * profile.trainingDays sessions (default 4, at most 7). The current week
 * adds to the streak once it reaches the target and never breaks it before
 * it ends.
 * @param {object} state reads logs, profile.trainingDays
 * @param {string} today workout day YYYY-MM-DD
 * @returns {{current:number, best:number, target:number, thisWeek:number}}
 *   thisWeek = sessions so far in the current week
 */
export function weeklyStreak(state, today) {
  const target = trainingTarget(state);
  const t = dayNumber(today);
  if (t === null) return { current: 0, best: 0, target, thisWeek: 0 };
  const days = trainingDays(state, t);
  const thisMonday = t - monIndex(t);
  const perWeek = new Map();
  days.forEach(n => {
    const monday = n - monIndex(n);
    perWeek.set(monday, (perWeek.get(monday) || 0) + 1);
  });
  let run = 0;
  let best = 0;
  if (days.length) {
    for (let monday = days[0] - monIndex(days[0]); monday <= thisMonday; monday += 7) {
      if ((perWeek.get(monday) || 0) >= target) {
        run += 1;
        best = Math.max(best, run);
      } else if (monday !== thisMonday) {
        run = 0;
      }
    }
  }
  return { current: run, best, target, thisWeek: perWeek.get(thisMonday) || 0 };
}

/**
 * This Mon-Sun week against the plan, counting only what could be done by
 * now: today joins plannedSoFar once it has a session, else it is still to
 * go. plannedSoFar + remaining = plannedWeek.
 * @param {object} state reads logs, plan, sessions, cycleAnchor
 * @param {string} today workout day YYYY-MM-DD
 * @returns {{done:number, plannedSoFar:number, plannedWeek:number, remaining:number}}
 *   done = sessions Monday..today, extra sessions on rest days included
 */
export function complianceSoFar(state, today) {
  const out = { done: 0, plannedSoFar: 0, plannedWeek: 0, remaining: 0 };
  const t = dayNumber(today);
  if (t === null) return out;
  const monday = t - monIndex(t);
  const fallback = keyOf(monday);
  for (let n = monday; n < monday + 7; n += 1) {
    const key = keyOf(n);
    const p = planDayFor(state, key, fallback);
    const planned = !!p && isObj(p.day) && !p.day.isRest;
    const trained = n <= t && isTrainingDate(state, key);
    if (planned) out.plannedWeek += 1;
    if (trained) out.done += 1;
    if (planned && (n < t || trained)) out.plannedSoFar += 1;
    else if (planned) out.remaining += 1;
  }
  return out;
}

/**
 * Planned training days without a session, oldest first. Dates before the
 * first logged session are skipped, and with `today` so are today and later.
 * @param {object} state reads logs, plan, sessions, cycleAnchor
 * @param {string} from YYYY-MM-DD, inclusive
 * @param {string} to YYYY-MM-DD, inclusive
 * @param {string} [today] workout day; also gives the fallback anchor (the
 *   Monday of its week, else of `to`'s week)
 * @returns {Array<{date:string, planDay:string|null}>} planDay = the day's name
 */
export function missedDays(state, from, to, today) {
  const f = dayNumber(from);
  let end = dayNumber(to);
  if (f === null || end === null) return [];
  const t = dayNumber(today);
  if (t !== null) end = Math.min(end, t - 1);
  const days = trainingDays(state, null);
  if (!days.length) return [];
  const fallback = mondayOf(t !== null ? today : to);
  const out = [];
  for (let n = Math.max(f, days[0]); n <= end; n += 1) {
    const key = keyOf(n);
    const p = planDayFor(state, key, fallback);
    if (!p || !isObj(p.day) || p.day.isRest || isTrainingDate(state, key)) continue;
    out.push({ date: key, planDay: typeof p.day.name === 'string' ? p.day.name : null });
  }
  return out;
}
